import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import IndentDataService from "../services/IndentService";
import BrandService from "../services/BrandService";
import ArticleService from "../services/ArticleService";
import { useTable, useSortBy } from "react-table";
import { useNavigate } from "react-router-dom";
import { ApplicationContext } from "./ApplicationContext";
import { formatDate, formatCurrency } from "./utils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/IndentList.css";
import { sr } from "date-fns/locale";
import { format as formatDateFns } from "date-fns";

registerLocale("sr", sr);

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Na cekanju", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-400", dot: "bg-amber-400", badgeBorder: "border-amber-200" },
  { value: "ACTIVATED", label: "Aktivirana", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-400", dot: "bg-emerald-500", badgeBorder: "border-emerald-200" },
  { value: "DELIVERED", label: "Isporucena", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-400", dot: "bg-blue-500", badgeBorder: "border-blue-200" },
  { value: "CANCELED", label: "Otkazana", bg: "bg-red-50", text: "text-red-700", border: "border-red-400", dot: "bg-red-500", badgeBorder: "border-red-200" },
];

const IndentsList = () => {
  const navigate = useNavigate();
  const { userRole } = useContext(ApplicationContext);

  // Data state
  const [indents, setIndents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Filter state
  const [customerName, setCustomerName] = useState("");
  const [code, setCode] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [selectedArticleId, setSelectedArticleId] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");

  // Dropdown data
  const [brands, setBrands] = useState([]);
  const [articles, setArticles] = useState([]);

  // Load dropdown data on mount
  useEffect(() => {
    BrandService.getAll()
      .then((res) => setBrands(res.data || []))
      .catch(() => {});
    ArticleService.getAllActiveArticles()
      .then((res) => setArticles(res.data || []))
      .catch(() => {});
  }, []);

  // Build params and fetch
  const fetchIndents = useCallback((page = 0, size = pageSize) => {
    setLoading(true);
    const params = {
      page,
      size,
      sortBy: "creationTime",
      sortDir: "desc",
    };

    if (customerName.trim()) params.customerName = customerName.trim();
    if (code.trim()) params.code = code.trim();
    if (startDate) params.dateFrom = formatDateFns(startDate, "yyyy-MM-dd");
    if (endDate) params.dateTo = formatDateFns(endDate, "yyyy-MM-dd");
    if (selectedStatuses.length > 0) params.status = selectedStatuses;
    if (selectedBrandId) params.brandId = selectedBrandId;
    if (selectedArticleId) params.articleId = selectedArticleId;
    if (amountMin) params.amountMin = amountMin;
    if (amountMax) params.amountMax = amountMax;

    IndentDataService.search(params)
      .then((response) => {
        const data = response.data;
        setIndents(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setCurrentPage(data.number ?? page);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Greska pri ucitavanju porudzbina.");
        setLoading(false);
      });
  }, [customerName, code, startDate, endDate, selectedStatuses, selectedBrandId, selectedArticleId, amountMin, amountMax, pageSize]);

  // Initial fetch on mount
  useEffect(() => {
    fetchIndents(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setCurrentPage(0);
    fetchIndents(0);
  };

  const handleReset = () => {
    setCustomerName("");
    setCode("");
    setStartDate(null);
    setEndDate(null);
    setSelectedStatuses([]);
    setSelectedBrandId("");
    setSelectedArticleId("");
    setAmountMin("");
    setAmountMax("");
    setCurrentPage(0);
    setLoading(true);
    IndentDataService.search({ page: 0, size: pageSize, sortBy: "creationTime", sortDir: "desc" })
      .then((response) => {
        const data = response.data;
        setIndents(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setCurrentPage(data.number ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Filter articles by selected brand
  const filteredArticles = useMemo(() => {
    if (!selectedBrandId) return articles;
    return articles.filter((a) => a.brand && String(a.brand.brand_id) === String(selectedBrandId));
  }, [articles, selectedBrandId]);

  // Clear article selection when brand changes and article doesn't belong to new brand
  const handleBrandChange = (brandId) => {
    setSelectedBrandId(brandId);
    if (brandId && selectedArticleId) {
      const articleStillValid = articles.some(
        (a) => String(a.article_id) === String(selectedArticleId) && a.brand && String(a.brand.brand_id) === String(brandId)
      );
      if (!articleStillValid) setSelectedArticleId("");
    }
  };

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const getActiveFilterChips = useCallback(() => {
    const chips = [];
    if (customerName.trim()) chips.push({ key: "customerName", label: `Klijent: ${customerName.trim()}` });
    if (code.trim()) chips.push({ key: "code", label: `ID: ${code.trim()}` });
    if (startDate || endDate) {
      const from = startDate ? formatDateFns(startDate, "dd.MM.yyyy") : "...";
      const to = endDate ? formatDateFns(endDate, "dd.MM.yyyy") : "...";
      chips.push({ key: "date", label: `${from} – ${to}` });
    }
    selectedStatuses.forEach((s) => {
      const opt = STATUS_OPTIONS.find((o) => o.value === s);
      if (opt) chips.push({ key: `status-${s}`, label: `Status: ${opt.label}` });
    });
    if (selectedBrandId) {
      const brand = brands.find((b) => String(b.brand_id) === String(selectedBrandId));
      chips.push({ key: "brand", label: `Brend: ${brand ? brand.brandName : selectedBrandId}` });
    }
    if (selectedArticleId) {
      const article = articles.find((a) => String(a.article_id) === String(selectedArticleId));
      chips.push({ key: "article", label: `Artikal: ${article ? article.name : selectedArticleId}` });
    }
    if (amountMin || amountMax) {
      chips.push({ key: "amount", label: `Iznos: ${amountMin || "0"} – ${amountMax || "∞"}` });
    }
    return chips;
  }, [customerName, code, startDate, endDate, selectedStatuses, selectedBrandId, selectedArticleId, amountMin, amountMax, brands, articles]);

  const removeFilter = (chipKey) => {
    // Compute new filter values before setting state
    const newVals = {
      customerName: chipKey === "customerName" ? "" : customerName,
      code: chipKey === "code" ? "" : code,
      startDate: chipKey === "date" ? null : startDate,
      endDate: chipKey === "date" ? null : endDate,
      statuses: chipKey.startsWith("status-")
        ? selectedStatuses.filter((s) => s !== chipKey.replace("status-", ""))
        : selectedStatuses,
      brandId: chipKey === "brand" ? "" : selectedBrandId,
      articleId: chipKey === "article" ? "" : selectedArticleId,
      amountMin: chipKey === "amount" ? "" : amountMin,
      amountMax: chipKey === "amount" ? "" : amountMax,
    };

    // Update state
    if (chipKey === "customerName") setCustomerName("");
    else if (chipKey === "code") setCode("");
    else if (chipKey === "date") { setStartDate(null); setEndDate(null); }
    else if (chipKey.startsWith("status-")) {
      setSelectedStatuses(newVals.statuses);
    }
    else if (chipKey === "brand") { setSelectedBrandId(""); setSelectedArticleId(""); }
    else if (chipKey === "article") setSelectedArticleId("");
    else if (chipKey === "amount") { setAmountMin(""); setAmountMax(""); }

    // Fetch with new values directly
    const params = { page: 0, size: pageSize, sortBy: "creationTime", sortDir: "desc" };
    if (newVals.customerName.trim()) params.customerName = newVals.customerName.trim();
    if (newVals.code.trim()) params.code = newVals.code.trim();
    if (newVals.startDate) params.dateFrom = formatDateFns(newVals.startDate, "yyyy-MM-dd");
    if (newVals.endDate) params.dateTo = formatDateFns(newVals.endDate, "yyyy-MM-dd");
    if (newVals.statuses.length > 0) params.status = newVals.statuses;
    if (newVals.brandId) params.brandId = newVals.brandId;
    if (newVals.articleId) params.articleId = newVals.articleId;
    if (newVals.amountMin) params.amountMin = newVals.amountMin;
    if (newVals.amountMax) params.amountMax = newVals.amountMax;

    setLoading(true);
    setCurrentPage(0);
    IndentDataService.search(params)
      .then((response) => {
        const data = response.data;
        setIndents(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setCurrentPage(data.number ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Actions
  const deleteIndent = (indent) => {
    if (!indent) return;
    IndentDataService.remove(indent.code)
      .then(() => {
        fetchIndents(currentPage);
        toast.success("Uspesno obrisana porudzbina! [" + indent.code + "]");
      })
      .catch((e) => toast.error(e));
  };

  const activateIndent = (indent) => {
    if (!indent) return;
    IndentDataService.activateIndent(indent.code)
      .then(() => {
        fetchIndents(currentPage);
        toast.success("Uspesno aktivirana porudzbina! [" + indent.code + "]");
      })
      .catch((e) => toast.error(e));
  };

  const confirmDelivery = (indent) => {
    if (!indent) return;
    IndentDataService.confirmIndentDelivery(indent.code)
      .then(() => {
        fetchIndents(currentPage);
        toast.success("Uspesno isporucena porudzbina! [" + indent.code + "]");
      })
      .catch((e) => toast.error(e));
  };

  const viewIndent = (indent) => {
    if (!indent) return;
    navigate(`/indents/entries/${indent.code}`);
  };

  // Page handlers
  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setCurrentPage(newPage);
    fetchIndents(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    fetchIndents(0, newSize);
  };

  // Table columns — widths tuned so all columns fit on desktop without scroll
  const columns = useMemo(() => {
    const cols = [];

    if (userRole !== "ROLE_USER") {
      cols.push({
        Header: "Sifra",
        accessor: "customerCode",
        width: 65,
        Cell: ({ value }) => <span className="text-gray-500 text-xs">{value}</span>,
      });
      cols.push({
        Header: "Klijent",
        accessor: "customerNameOfTheLegalEntity",
        width: 180,
        Cell: ({ row }) => (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-medium text-gray-900 truncate">{row.original.customerNameOfTheLegalEntity}</span>
            {row.original.customerType === "INFLUENCER" && (
              <span className="flex-shrink-0 px-1 py-0.5 rounded text-[9px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                INF
              </span>
            )}
          </div>
        ),
      });
    }

    cols.push(
      {
        Header: "ID",
        accessor: "code",
        width: 90,
        Cell: ({ value }) => <span className="font-semibold text-blue-600 text-xs">{value}</span>,
      },
      {
        Header: "Adresa",
        accessor: (row) => `${row.deliveryAddressCity || ""}, ${row.deliveryAddressLocation || ""}`,
        width: 160,
        Cell: ({ value }) => <span className="text-gray-500 truncate block" title={value}>{value}</span>,
      },
      {
        Header: "Status",
        accessor: "indentStatus",
        width: 110,
        Cell: ({ value }) => {
          const opt = STATUS_OPTIONS.find((o) => o.value === value);
          if (!opt) return value;
          return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${opt.bg} ${opt.text} ${opt.badgeBorder}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`}></span>
              {opt.label}
            </span>
          );
        },
      },
      {
        Header: "Datum",
        accessor: "creationTime",
        width: 120,
        Cell: ({ value }) => (
          <span className="font-medium text-gray-700 text-xs">{formatDate(value, true)}</span>
        ),
        sortType: (rowA, rowB) => {
          const a = new Date(rowA.values.creationTime);
          const b = new Date(rowB.values.creationTime);
          return a.getTime() - b.getTime();
        },
      },
      {
        Header: "",
        accessor: "actions",
        disableSortBy: true,
        width: 150,
        Cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              onClick={() => viewIndent(row.original)}
              title="Pregled"
              className="px-2 py-1 bg-blue-600 text-white text-[11px] font-medium rounded hover:bg-blue-700 focus:outline-none"
            >
              Pregled
            </button>
            {row.original.indentStatus === "PENDING" && (
              <button
                onClick={() => deleteIndent(row.original)}
                title="Izbrisi"
                className="p-1 text-red-500 hover:bg-red-50 rounded focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </button>
            )}
            {row.original.indentStatus === "PENDING" && (userRole === "ROLE_FAKTURISTA" || userRole === "ROLE_ADMIN") && (
              <button
                onClick={() => activateIndent(row.original)}
                title="Aktiviraj"
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </button>
            )}
            {row.original.indentStatus === "ACTIVATED" && (userRole === "ROLE_MAGACIONER" || userRole === "ROLE_ADMIN" || userRole === "ROLE_FAKTURISTA") && (
              <button
                onClick={() => confirmDelivery(row.original)}
                title="Potvrdi isporuku"
                className="p-1 text-purple-600 hover:bg-purple-50 rounded focus:outline-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </button>
            )}
          </div>
        ),
      }
    );

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole]);

  const tableInstance = useTable(
    {
      columns,
      data: indents,
      initialState: {
        sortBy: [{ id: "creationTime", desc: true }],
      },
    },
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  // Calculate display range
  const rangeStart = totalElements > 0 ? currentPage * pageSize + 1 : 0;
  const rangeEnd = Math.min((currentPage + 1) * pageSize, totalElements);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Filter Panel */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        {/* Collapsed Header */}
        <div className="px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-sm font-semibold text-gray-900">Filteri</span>
            </div>
            {getActiveFilterChips().map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1 text-xs font-medium"
              >
                {chip.label}
                <button
                  onClick={(e) => { e.stopPropagation(); removeFilter(chip.key); }}
                  className="ml-0.5 text-blue-400 hover:text-blue-600"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-1.5 text-blue-600 text-sm font-medium hover:text-blue-700"
          >
            {filtersOpen ? "Sakrij filtere" : "Prikazi filtere"}
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Expanded Filter Body */}
        {filtersOpen && (
          <div className="border-t border-gray-100 px-5 py-5">
            {/* Row 1: 4-column grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {userRole !== "ROLE_USER" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Klijent</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Ime klijenta"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">ID Porudzbine</label>
                <input
                  type="text"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Sifra porudzbine"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Period</label>
                <div className="datepicker-wrapper">
                  <svg className="datepicker-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      setStartDate(update[0]);
                      setEndDate(update[1]);
                    }}
                    dateFormat="dd.MM.yyyy"
                    locale="sr"
                    isClearable
                    placeholderText="Od – Do"
                    className="datepicker-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Brend</label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={selectedBrandId}
                  onChange={(e) => handleBrandChange(e.target.value)}
                >
                  <option value="">Svi brendovi</option>
                  {brands.map((b) => (
                    <option key={b.brand_id} value={b.brand_id}>{b.brandName}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Article + Amount */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Artikal
                  {selectedBrandId && (
                    <span className="ml-1.5 normal-case tracking-normal font-medium text-blue-600">
                      ({brands.find((b) => String(b.brand_id) === String(selectedBrandId))?.brandName})
                    </span>
                  )}
                </label>
                <select
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={selectedArticleId}
                  onChange={(e) => setSelectedArticleId(e.target.value)}
                >
                  <option value="">{selectedBrandId ? `Svi artikli brenda` : "Svi artikli"}</option>
                  {filteredArticles.map((a) => (
                    <option key={a.article_id} value={a.article_id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Iznos</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Min"
                    value={amountMin}
                    onChange={(e) => setAmountMin(e.target.value)}
                  />
                  <span className="text-gray-400 font-medium">–</span>
                  <input
                    type="number"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Max"
                    value={amountMax}
                    onChange={(e) => setAmountMax(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Row 3: Status + Buttons */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => toggleStatus(opt.value)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        selectedStatuses.includes(opt.value)
                          ? `${opt.bg} ${opt.text} border-2 ${opt.border}`
                          : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {selectedStatuses.includes(opt.value) && "✓ "}{opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:ml-auto flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 sm:flex-none px-4 py-2.5 bg-white border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
                >
                  Reset
                </button>
                <button
                  onClick={handleSearch}
                  className="flex-1 sm:flex-none px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shadow-sm"
                >
                  Pretraga
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : indents.length === 0 ? (
          <div className="flex justify-center items-center min-h-[200px] text-gray-400 text-sm">
            Nema rezultata za zadate filtere.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table {...getTableProps()} className="w-full table-fixed">
                <colgroup>
                  {headerGroups[0]?.headers.map((column) => (
                    <col key={column.id} style={{ width: column.width || "auto" }} />
                  ))}
                </colgroup>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()} className="border-b-2 border-gray-200">
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          className="px-3 py-2.5 text-left text-[11px] font-bold text-gray-900 uppercase tracking-wide cursor-pointer select-none"
                        >
                          <div className="flex items-center gap-1">
                            {column.render("Header")}
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <svg className="w-3 h-3 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>
                              ) : (
                                <svg className="w-3 h-3 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"/></svg>
                              )
                            ) : column.canSort ? (
                              <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>
                            ) : null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={`border-b border-gray-100 hover:bg-blue-50/50 transition-colors duration-150 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/60"}`}
                      >
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-3 py-2.5 text-sm text-gray-700 overflow-hidden text-ellipsis"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card layout */}
            <div className="lg:hidden divide-y divide-gray-100">
              {rows.map((row, i) => {
                prepareRow(row);
                const d = row.original;
                const statusOpt = STATUS_OPTIONS.find((o) => o.value === d.indentStatus);
                return (
                  <div key={row.id} className={`px-4 py-3 space-y-2 ${i % 2 !== 0 ? "bg-gray-50/60" : ""}`}>
                    {/* Row 1: Code + Status */}
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-blue-600 text-sm">{d.code}</span>
                      {statusOpt && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${statusOpt.bg} ${statusOpt.text} ${statusOpt.badgeBorder}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusOpt.dot}`}></span>
                          {statusOpt.label}
                        </span>
                      )}
                      <span className="ml-auto text-xs text-gray-400">{formatDate(d.creationTime, true)}</span>
                    </div>
                    {/* Row 2: Client + Address */}
                    {userRole !== "ROLE_USER" && (
                      <p className="text-sm text-gray-900 font-medium">
                        {d.customerNameOfTheLegalEntity}
                        {d.customerType === "INFLUENCER" && (
                          <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">INF</span>
                        )}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {d.deliveryAddressCity}{d.deliveryAddressLocation ? `, ${d.deliveryAddressLocation}` : ""}
                    </p>
                    {/* Row 3: Actions */}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => viewIndent(d)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700"
                      >
                        Pregled
                      </button>
                      {d.indentStatus === "PENDING" && (
                        <button onClick={() => deleteIndent(d)} className="px-3 py-1.5 bg-white text-red-600 border border-red-200 text-xs font-medium rounded-md hover:bg-red-50">
                          Izbrisi
                        </button>
                      )}
                      {d.indentStatus === "PENDING" && (userRole === "ROLE_FAKTURISTA" || userRole === "ROLE_ADMIN") && (
                        <button onClick={() => activateIndent(d)} className="px-3 py-1.5 bg-white text-emerald-600 border border-emerald-200 text-xs font-medium rounded-md hover:bg-emerald-50">
                          Aktiviraj
                        </button>
                      )}
                      {d.indentStatus === "ACTIVATED" && (userRole === "ROLE_MAGACIONER" || userRole === "ROLE_ADMIN" || userRole === "ROLE_FAKTURISTA") && (
                        <button onClick={() => confirmDelivery(d)} className="px-3 py-1.5 bg-white text-purple-600 border border-purple-200 text-xs font-medium rounded-md hover:bg-purple-50">
                          Potvrdi
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="px-5 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
          {/* Mobile pagination */}
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prethodna
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sledeca
            </button>
          </div>
          {/* Desktop pagination */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <p className="text-sm text-gray-500">
              Prikazano{" "}
              <span className="font-semibold text-gray-900">{rangeStart}</span>
              {" "}–{" "}
              <span className="font-semibold text-gray-900">{rangeEnd}</span>
              {" "}od{" "}
              <span className="font-semibold text-gray-900">{totalElements}</span>
              {" "}rezultata
            </p>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {[10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} po stranici
                  </option>
                ))}
              </select>
              <nav className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0}
                  className="px-2.5 py-1.5 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed border-r border-gray-300"
                >
                  «
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-2.5 py-1.5 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed border-r border-gray-300"
                >
                  ‹
                </button>
                <span className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold">
                  {currentPage + 1}
                </span>
                <span className="px-2 py-1.5 bg-white text-sm text-gray-500">
                  / {totalPages || 1}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2.5 py-1.5 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed border-l border-gray-300"
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2.5 py-1.5 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed border-l border-gray-300"
                >
                  »
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndentsList;
